import { Validator } from "vee-validate";
import { getType } from "mime";

/**
 * カスタムバリデーションクラス
 *
 * カスタムバリデーションを追加する場合は、privateで実装して、addCustomValidate()から呼び出すようにする
 * 各コンポーネントはaddCustomValidate()を呼び出すだけでよい
 */
export default class CustomValidate {
  /**
   * カスタムバリデーションを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  public static addCustomValidate(validator: Validator) {
    this.addExistDateValidate(validator);
    this.addCareimageValidate(validator);
    this.addJpegPngImageValidate(validator);
    this.addJpegPngMp4MovFileValidate(validator);
    this.addPdfDocumentValidate(validator);
    this.addMp4TimeValidate(validator);
    this.addFileSizeValidate(validator);
    this.addFileSizeOverValidate(validator);
    this.addTelValidate(validator);
    this.addNumericValidate(validator);
    this.addHalfAlphaNumValidate(validator);
    this.addHiraganaValidate(validator);
    this.addHiraganaAndNumbersValidate(validator);
    this.addHourFromtToValidate(validator);
    this.addEqualValueValidate(validator);
    this.addNeedTrueValidate(validator);
    this.addNeedFalseValidate(validator);
    this.addNonExistUppercase(validator);
    this.addAtLeastOneTrue(validator);
    this.addLinkUrlValidate(validator);
    this.addDecimalValidate(validator);

    return;
  }

  /**
   * 存在する日付かのバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addExistDateValidate(validator: Validator) {
    validator.extend("exist_date", {
      getMessage: (field: string) => `${field}は存在しない年月日です`,
      validate(val: any, ...args: Array<any>) {
        // 空 & 数値チェック
        if (!val || isNaN(val)) {
          return false;
        }

        // Date型にする
        const date: string = String(val);
        const year: number = Number(date.substr(0, 4));
        const month: number = Number(date.substr(4, 2)) - 1;
        const day: number = Number(date.substr(6, 2));
        const targetDate: Date = new Date(year, month, day);

        // Date型に変換後、年月日を比較して異なる場合はエラー
        if (targetDate.getFullYear() != year) {
          return false;
        } else if (targetDate.getMonth() != month) {
          return false;
        } else if (targetDate.getDate() != day) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * 介護記録(jpeg)かのバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addCareimageValidate(validator: Validator) {
    validator.extend("careimage", {
      getMessage: (field: string) => `${field}はjpgまたはpdf以外の拡張子のため登録できません`,
      validate(val: File, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものがFileでなければエラー
        if (!(val instanceof File)) {
          return false;
        }

        // ファイルのメディアタイプチェック
        // APIと同じライブラリでチェックすることで整合性を担保している
        const mime: string | null = CustomValidate.getType(val.name);
        if (mime == null || mime != val.type) {
          return false;
        }

        // image/jpegでなければエラー
        if (!val.type.match("image/jpeg") && !val.type.match("application/pdf")) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * ## ファイルのパスまたは拡張子からContent-Typeを取得する
   */
  static getType(fileString: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileString[0] == ".") {
      contentType = getType(`file${fileString}`);
    } else {
      contentType = getType(`${fileString}`);
    }

    return contentType;
  }

  /**
   * mp4の時間バリデーションチェックを追加する(単位は分)
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addMp4TimeValidate(validator: Validator) {
    validator.extend("mp4_time_limit", {
      getMessage: (field: string, ...args) => `${field}は時間が${args[0]}分を超えているため登録できません`,
      async validate(val: File, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものがFileでなければエラー
        if (!(val instanceof File)) {
          return false;
        }

        // mp4を読込んで、指定時間以内でなければエラー
        const videoLoad = new Promise<boolean>((resolve) => {
          const video: HTMLVideoElement = document.createElement("video");
          video.addEventListener("loadedmetadata", () => {
            const durationLimit: number = 60 * Number(args[0]);
            if (durationLimit < video.duration) {
              resolve(false);
            } else {
              resolve(true);
            }
          });
          const url: string = window.URL.createObjectURL(val);
          video.src = url;
          video.load();
        });

        const result: boolean = await videoLoad;

        return result;
      }
    });

    return;
  }

  /**
   * jpegかpngかのバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addJpegPngImageValidate(validator: Validator) {
    validator.extend("jpeg_png_image", {
      getMessage: (field: string) => `${field}はjpgまたはpng以外の拡張子は指定できません`,
      validate(val: File, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものがFileでなければエラー
        if (!(val instanceof File)) {
          return false;
        }

        // ファイルのメディアタイプチェック
        // APIと同じライブラリでチェックすることで整合性を担保している
        const mime: string | null = CustomValidate.getType(val.name);
        if (mime == null || mime != val.type) {
          return false;
        }

        // image/jpegまたはimage/pngでなければエラー
        if (!val.type.match("image/jpeg") && !val.type.match("image/png")) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * バリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addJpegPngMp4MovFileValidate(validator: Validator) {
    validator.extend("jpeg_png_mp4_mov_file", {
      getMessage: (field: string) => `${field}は画像（jpgまたはpng）、動画（mp4またはmov）以外の拡張子は指定できません`,
      validate(val: File, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものがFileでなければエラー
        if (!(val instanceof File)) {
          return false;
        }

        // ファイルのメディアタイプチェック
        // APIと同じライブラリでチェックすることで整合性を担保している
        const mime: string | null = CustomValidate.getType(val.name);
        if (mime == null || mime != val.type) {
          return false;
        }

        // video/mp4,image/jpeg,image/pngでなければエラー
        if (!val.type.match("image/jpeg") && !val.type.match("image/png") && !val.type.match("video/mp4") && !val.type.match("video/quicktime")) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * バリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addPdfDocumentValidate(validator: Validator) {
    validator.extend("pdf_document", {
      getMessage: (field: string) => `${field}はpdf以外の拡張子は指定できません`,
      validate(val: File, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものがFileでなければエラー
        if (!(val instanceof File)) {
          return false;
        }

        // ファイルのメディアタイプチェック
        // APIと同じライブラリでチェックすることで整合性を担保している
        const mime: string | null = CustomValidate.getType(val.name);
        if (mime == null || mime != val.type) {
          return false;
        }

        // application/pdfでなければエラー
        if (!val.type.match("application/pdf")) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * サイズのバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addFileSizeValidate(validator: Validator) {
    validator.extend("file_size", {
      getMessage: (field: string, ...args: Array<any>) => `${field}のファイルサイズが${args[0]}MBを超えているため登録できません`,
      validate(val: File, ...args: Array<any>) {
        // サイズを取得して比較し、規定サイズ以下ならエラー
        // 表示はmbyteにするが、サイズ比較はbyteで計算したいので、1024*1024*引数値とする
        const fileSize: number = 1024 * 1024 * Number(args[0]);

        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものがFileでなければエラー
        if (!(val instanceof File)) {
          return false;
        }

        if (val && fileSize < val.size) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * サイズのバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addFileSizeOverValidate(validator: Validator) {
    validator.extend("file_size_over", {
      getMessage: (field: string, ...args: Array<any>) => `${field}のファイルサイズが大きすぎるため登録できません`,
      validate(val: File, ...args: Array<any>) {
        // サイズを取得して比較し、規定サイズ以下ならエラー
        // 表示はmbyteにするが、サイズ比較はbyteで計算したいので、1024*1024*引数値とする
        const fileSize: number = 1024 * 1024 * Number(args[0]);

        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものがFileでなければエラー
        if (!(val instanceof File)) {
          return false;
        }

        if (val && fileSize < val.size) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * 電話番号のバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addTelValidate(validator: Validator) {
    validator.extend("tel", {
      getMessage: (field: string, ...args: Array<any>) => `${field}は半角数字または-のみ入力可能です`,
      validate(val: string, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものに数字、-以外が含まれていればエラー
        const regex = new RegExp("^[0-9\\-]+$");
        if (!val.match(regex)) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * 半角数字のバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addNumericValidate(validator: Validator) {
    validator.extend("custom_numeric", {
      getMessage: (field: string, ...args: Array<any>) => `${field}は半角数字のみ入力可能です`,
      validate(val: string, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものに数字以外が含まれていればエラー
        const regex = new RegExp("^[0-9]+$");
        if (!val.match(regex)) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * 半角英数字のバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addHalfAlphaNumValidate(validator: Validator) {
    validator.extend("half_alpha_num", {
      getMessage: (field: string, ...args: Array<any>) => `${field}は半角英数字のみ入力可能です`,
      validate(val: string, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものに半角英数字以外が含まれていればエラー
        const regex = new RegExp("^[0-9a-zA-Z]+$");
        if (!val.match(regex)) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * ひらがなのバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addHiraganaValidate(validator: Validator) {
    validator.extend("hiragana", {
      getMessage: (field: string, ...args: Array<any>) => `${field}はひらがなのみ入力可能です`,
      validate(val: string, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものにひらがな以外が含まれていればエラー
        const regex = new RegExp("^[ぁ-んー　]*$");
        if (!val.match(regex)) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * ひらがなと全角・半角数字のみを許容するバリデーションルールを追加します。
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス（extend関数でも代用可）
   */
  private static addHiraganaAndNumbersValidate(validator: Validator) {
    validator.extend("hiraganaAndNumbers", {
      getMessage: (field: string) => `${field}はひらがなと数字のみ入力可能です`,
      validate(val: string): boolean {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // ひらがな、長音符、全角スペース、半角数字、全角数字、半角スペースを許容する正規表現
        const regex = new RegExp("^[ぁ-んー　0-9０-９ ]*$");

        // val が正規表現にマッチしない場合はエラー (false) を返す
        if (!regex.test(val)) {
          return false;
        }

        // すべての条件を満たせば成功 (true) を返す
        return true;
      }
    });
  }

  /**
   * 期間のバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addHourFromtToValidate(validator: Validator) {
    validator.extend("hour_from_to", {
      getMessage: (field: string, ...args: Array<any>) => `時間帯の期間が不正です`,
      validate(val: string, ...args: Array<any>) {
        if (!val || !args[0][0]) {
          return true;
        }

        // 時刻は時分の形式(例 00:20)の可能性もあるので、「:」を削除
        const timeFrom: number = Number(val.replace(/:/g, ""));
        const timeTo: number = Number(args[0][0].replace(/:/g, ""));

        if (timeTo <= timeFrom) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * 値が同一かのバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addEqualValueValidate(validator: Validator) {
    validator.extend(
      "equal_value",
      {
        getMessage: (field: string, ...args: Array<any>) => `${field}と${field}（確認）が異なります`,
        validate(val: string, ...args: Array<any>) {
          if (!val && !args[0][0]) {
            return true;
          }

          if (val != args[0][0]) {
            return {
              valid: false,
              data: {
                required: true
              }
            };
          }

          return true;
        }
      },
      { computesRequired: true }
    );

    return;
  }

  private static addNeedTrueValidate(validator: Validator) {
    validator.extend("need_true", {
      getMessage: (field: string, ...args: Array<any>) => `${field}はチェックのみ入力可能です`,
      validate(val: boolean, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (val == null) {
          return true;
        }

        if (val !== true) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  private static addNeedFalseValidate(validator: Validator) {
    validator.extend("need_false", {
      getMessage: (field: string, ...args: Array<any>) => `${field}はチェック無しのみ入力可能です`,
      validate(val: boolean, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (val == null) {
          return true;
        }

        if (val != false) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * ### 英大文字を使用していないかチェックする
   */
  private static addNonExistUppercase(validator: Validator) {
    validator.extend("non_uppercase", {
      getMessage: (field: string, ...args: Array<any>) => `${field}は英大文字は使用できません`,
      validate(val: string, ...args: Array<any>) {
        // valが存在しない場合は無視する
        if (!val) {
          return true;
        }

        // 渡されてきたものに英大文字が含まれていればエラー
        const regex = new RegExp(/[A-Z]/);
        if (val.match(regex)) {
          return false;
        }

        return true;
      }
    });

    return;
  }

  /**
   * ### 配列内に1つ以上trueが存在するかチャックする
   */
  private static addAtLeastOneTrue(validator: Validator) {
    validator.extend("atLeastOneTrue", {
      getMessage: (field: string, ...args: Array<any>) => `${field}は1つ以上選択する必要があります`,
      validate: (value, args) => {
        // valueが配列であり、かつその中にtrueが1つでも含まれているかを確認
        return Array.isArray(value) && value.some((element) => element === true);
      }
    });

    return;
  }

  /**
   * メッセージ送信のような[リンク名](リンクURL)のリンクURLが有効なURLかバリデーションする
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addLinkUrlValidate(validator: Validator) {
    // リンク名表記のURLバリデーションを追加
    validator.extend("valid_link_url", {
      getMessage: (field) => "リンク名の表記「[リンク名](URL)」のURLが正しくありません。（例: http://... または https://...）",
      validate: (value) => {
        if (!value) return true;
        // [リンク名](URL) の形式を抽出する正規表現
        const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let match;
        while ((match = regex.exec(value)) !== null) {
          const url = match[2].trim();
          // URLがhttp://またはhttps://から始まり、空白を含まないかをチェック
          if (!/^https?:\/\/\S+$/.test(url)) {
            return false;
          }
        }
        return true;
      }
    });
  }

  /**
   * 半角数字と半角ドット(.)のバリデーションチェックを追加する
   *
   * @param validator バリデーション追加対象のVeeValidatorインスタンス
   */
  private static addDecimalValidate(validator: Validator) {
    validator.extend("custom_decimal", {
      getMessage: (field: string) => `${field}は半角数字、または半角の「.」のみ入力可能です`,
      validate(val: string): boolean {
        if (!val) {
          return true;
        }

        const regex = /^[0-9.]+$/;
        return regex.test(val);
      }
    });
  }
}
